import "./App.css";
import "./styles/loader.css";
import "react-toastify/dist/ReactToastify.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-theme-material.css";
import * as AgGrid from "ag-grid-enterprise";
import { GridChartsModule } from "@ag-grid-enterprise/charts";
import { QueryClientProvider } from "react-query";
import { Dialog, ThemeProvider } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import Router from "./routes";
import ScrollToTop from "./components/shared/ScrollToTop";
import { muiTheme } from "./theme/mui";
import { queryClient } from "./queries";
import AlertDialog from "./components/shared/AlertDialog";
import { useDialogStore } from "./store/alertDialog";
import { useAuthStore, useAuthorizationStore } from "./store";
import HandleHissaRedirect from "./components/shared/HandleHissaRedirect";
import DialogContainer from "./components/DialogContainer";
import { amplitudeInit, handleClickForTracking } from "./amplitudeAnalytics";
import { usePermissionStore } from "./store/permissionStore";
import { WarningModal } from "./components/utils";
import { optionsAdminAuth } from "./api/adminAuthMode";
import { startOptionsTokenSharing } from "./api/adminAuth";
import { safeReturnUrl } from "./utils/returnUrl";

const agGridLicenseKey = process.env.REACT_APP_AG_LICENSE_KEY || "";
AgGrid.LicenseManager.setLicenseKey(agGridLicenseKey);

function App() {
  const isMcpAuthorize = window.location.pathname === "/mcp/authorize";
  const mcpReturnUrl = safeReturnUrl(
    new URLSearchParams(window.location.search).get("returnUrl")
  );
  const isMcpUtilityPage =
    isMcpAuthorize ||
    (optionsAdminAuth &&
      window.location.pathname === "/login" &&
      mcpReturnUrl?.startsWith("/mcp/authorize") === true);
  amplitudeInit();
  useEffect(() => {
    if (!isMcpUtilityPage) {
      queryClient.prefetchQuery("vestingTemplates");
    }
  });
  useAuthStore();
  useAuthorizationStore();
  usePermissionStore();

  const channelRef = useRef<BroadcastChannel | undefined>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => startOptionsTokenSharing(), []);

  useEffect(() => {
    if (isMcpUtilityPage) return undefined;
    const tabId = sessionStorage.getItem("tabId") || generateRandomString(8);
    sessionStorage.setItem("tabId", tabId);
    const channel = new BroadcastChannel("single-tab-check");
    channelRef.current = channel;
    let isFirstTab = true;
    channel.onmessage = (event) => {
      const { type, senderId } = event.data;
      if (senderId === tabId) {
        return;
      }
      if (type === "anyone-there?") {
        channel.postMessage({ type: "i-am-here", senderId: tabId });
      }
      if (type === "i-am-here") {
        isFirstTab = false;
        setShowModal(true);
      }
    };
    channel.postMessage({ type: "anyone-there?", senderId: tabId });
    const timer = setTimeout(() => {
      if (!isFirstTab) {
        setShowModal(true);
      }
    }, 500);
    return () => {
      clearTimeout(timer);
      channel.close();
    };
  }, [isMcpUtilityPage]);
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ScrollToTop />
        <HandleHissaRedirect />
        <ThemeProvider theme={muiTheme}>
          <div
            onClick={(e) => {
              handleClickForTracking(e);
            }}
          >
            {showModal && !isMcpUtilityPage ? (
              <WarningModal open={showModal} />
            ) : (
              <Router />
            )}
          </div>
          <ToastContainer position="bottom-right" autoClose={2000} />
          <AlertDialog {...useDialogStore().dialog} />
          <DialogContainer />
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
}

function generateRandomString(length: number) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
}

export default App;
